import type { DomainConfiguration } from '../ConfigurationMapper';
import type { RulesContext } from './context';
import { applySystemTypeTransition } from './transitions';
import {
  applyAtLeastOneStorage, applyE2ECompression, applyEncodingReconcile, applyJbodEol,
  applyNodeSizing, applySkuReset, applyToggleExclusion,
} from './storageRules';
import {
  applyConsultationMode, applyPricingOnQuarterService, applyPricingOnRegistration,
  applyServiceOptionReset, applySubscriptionDurationSnap,
} from './pricingRules';
import { applyMlecEligibility } from './mlecRules';
import { applyDualCpuFlashEligibility } from './dualCpuRules';

const SIDES = ['hybrid', 'afa'] as const;

// Rule-major orchestrator. Helpers are wired in Tasks 5–9; remaining steps are identity
// until ported.
export function applyRulesFromDomain(
  prev: DomainConfiguration, next: DomainConfiguration, ctx: RulesContext,
): DomainConfiguration {
  applySystemTypeTransition(prev, next, ctx);
  const isExpansion = next.system.systemType === 'Expansion';
  // Rule 0 (node sizing) runs for BOTH system types — legacy has it in both
  // applyExpansionBusinessRules and applyBaseSystemBusinessRules.
  for (const side of SIDES) applyNodeSizing(prev, next, side, ctx);
  // Rules 1, 2, 3, 6 are ALL BaseSystem-only: legacy places them exclusively inside
  // applyBaseSystemBusinessRules (:341-404); the Expansion path has none of them.
  if (!isExpansion) {
    for (const side of SIDES) applyEncodingReconcile(prev, next, side);   // R1
    applyJbodEol(next);                                                  // R2
    applyAtLeastOneStorage(next);                                        // R3
    for (const side of SIDES) applyToggleExclusion(prev, next, side);    // R6
  }
  // The remaining shared rules (R8b, R9/10/11, R4, R5, R7, R8) are SHARED: legacy places them
  // in applySharedBusinessRules, called by BOTH the Expansion and BaseSystem branches — so
  // these run regardless of systemType. Keep them OUTSIDE the `if (!isExpansion)` gate above.
  // Legacy order: R8b -> R9 -> R10 -> R11 -> R4 -> R5 -> R7 -> R8.
  applyServiceOptionReset(next, ctx);                 // R8b
  applySkuReset(prev, next, 'hybrid', 'hdd', ctx);    // R9
  applySkuReset(prev, next, 'hybrid', 'ssd', ctx);    // R10
  applySkuReset(prev, next, 'afa', 'ssd', ctx);       // R11
  applyMlecEligibility(next, ctx);                    // R12
  applyDualCpuFlashEligibility(next, ctx);            // R13 (CONF-210, shared; see dualCpuRules)
  applySubscriptionDurationSnap(next, ctx);           // duration snap (before R4)
  applyPricingOnQuarterService(prev, next, ctx);      // R4
  applyPricingOnRegistration(prev, next, ctx);        // R5
  applyConsultationMode(next, ctx);                   // R7
  applyE2ECompression(next);                          // R8
  return next;
}
